import LocalDate from '@booking-services/localDate'
import {findLastIndex, sortBy, groupBy, flatten, sum, uniq} from 'lodash-es'

export default ({paymentSchedule, departureDate, clock}) => {
  paymentSchedule = sortBy(paymentSchedule, 'paymentDueDate')

  if (allPaymentsAreDueWithin45Days({paymentSchedule, departureDate})) {
    return combineIntoOnePayment({
      paymentSchedule,
      paymentType: 'Final Payment',
      paymentDueDate: LocalDate.fromDate(clock.getDate()),
    })
  }

  if (allPaymentDueDatesAreTheSame(paymentSchedule)) {
    return combineIntoOnePayment({
      paymentSchedule,
      paymentType: 'Final Payment',
      paymentDueDate: paymentSchedule[0].paymentDueDate,
    })
  }

  return combinePaymentsUpToLastDeposit(
    groupPaymentsByDateAndProduct({paymentSchedule, clock})
  )
}

function combineIntoOnePayment ({paymentSchedule, paymentType, paymentDueDate}) {
  return [{
    paymentAmount: sum(paymentSchedule.map(p => p.paymentAmount)),
    commission: sum(paymentSchedule.map(p => p.commission)),
    gstVat: sum(paymentSchedule.map(p => p.gstVat)),
    paymentDueDate,
    paymentType: paymentType,
    products: uniq(flatten(paymentSchedule.map(p => p.products))).sort(),
  }]
}

function groupPaymentsByDateAndProduct ({paymentSchedule}) {
  const dueDateGroups = groupBy(paymentSchedule, 'paymentDueDate')
  const sortedDates = Object.keys(dueDateGroups).sort()
  return flatten(sortedDates.map((date, index) => {
    const dateGroup =  dueDateGroups[date]
    const productDateGroups = groupBy(dateGroup, 'products')
    const combinedProductGroups = flatten(Object.values(productDateGroups).map(productDateGroup => {
      return combineIntoOnePayment({
        paymentSchedule: productDateGroup,
        paymentDueDate: productDateGroup[0] && productDateGroup[0].paymentDueDate,
        paymentType: paymentTypeForGroup({
          group: productDateGroup,
          index,
          groupCount: sortedDates.length,
          preferFinalPayments: true,
          allowMultipleDeposits: true,
        }),
      })
    }))
    return combineIntoOnePayment({
      paymentSchedule: combinedProductGroups,
      paymentDueDate: combinedProductGroups[0] && combinedProductGroups[0].paymentDueDate,
      paymentType: paymentTypeForGroup({
        group: combinedProductGroups,
        index,
        groupCount: sortedDates.length,
        preferFinalPayments: false,
        allowMultipleDeposits: true,
      }),
    })
  }))
}

function combinePaymentsUpToLastDeposit (paymentSchedule) {
  const deposits = paymentSchedule.filter(g => g.paymentType === 'Deposit')

  if (!deposits.length) return paymentSchedule

  const lastDepositIndex = findLastIndex(paymentSchedule, {'paymentType': 'Deposit'})
  const depositsToCombine = paymentSchedule.slice(0, lastDepositIndex + 1)
  const others = paymentSchedule.slice(lastDepositIndex + 1)

  let groupedSchedule = [
    ...combineIntoOnePayment({
      paymentSchedule: depositsToCombine,
      paymentType: 'Deposit',
      paymentDueDate: depositsToCombine.map(p => p.paymentDueDate).sort()[0],
    }),
    ...others,
  ]
  const depositCommission = groupedSchedule[0].commission
  groupedSchedule[0].commission = 0
  groupedSchedule[groupedSchedule.length -1].commission += depositCommission
  groupedSchedule.forEach((group,index) => {
    group.paymentType = paymentTypeForGroup({
      group: [group],
      index,
      groupCount: groupedSchedule.length,
      allowMultipleDeposits: false,
    })
  })
  return groupedSchedule
}

function paymentTypeForGroup ({group, index, groupCount, allowMultipleDeposits, preferFinalPayments}) {
  const containsDeposit = group.map(p => p.paymentType).includes('Deposit')
  const containsFinalPayment = group.map(p => p.paymentType).includes('Final Payment')

  if (preferFinalPayments && containsFinalPayment) {
    return 'Final Payment'
  }
  if ((allowMultipleDeposits || index === 0) && groupCount > 1 && containsDeposit) {
    return 'Deposit'
  }
  if (index > 0 && index < (groupCount - 1)) {
    return 'Interim Payment'
  }
  return 'Final Payment'
}

function allPaymentsAreDueWithin45Days ({paymentSchedule, departureDate}) {
  const paymentsWithin45DaysOfDeparture = paymentSchedule.filter(p =>
    p.paymentDueDate && p.paymentDueDate.differenceInDays(departureDate)  <= 45
  )
  const combineSchedule = paymentsWithin45DaysOfDeparture.length === paymentSchedule.length

  return combineSchedule
}

function allPaymentDueDatesAreTheSame (paymentSchedule) {
  const combineSchedule = paymentSchedule.map(p => p.paymentDueDate).every((val, i, arr) =>
    !val || !arr[0] ||
    val.toString() === arr[0].toString()
  )

  return combineSchedule
}
